import { BreadcrumbGroup, Link } from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import { StageGraph } from '../../components/stages/StageGraph';
import { ChatMessage, ScenarioWithRoomsAndStages } from '../../common/types';
import ChatBox from '../../components/chat/ChatBox';
import { useColors } from '../../common/chat/userColors';
import { useAuthors } from '../../common/chat/authors';
import { room, scenario } from './examples';
import { ChatBar } from '../../components/chat/ChatBar';
import { useRef } from 'react';

const logoUrl = 'https://cdn.aiescape.io/logo-fit.png';

export default function View() {
  const navigate = useNavigate();
  const messageAuthors = useAuthors(room.messages);
  // Can't just use members, because people can join and leave. Don't like graying out the names of people who left.
  // But also not the worst idea.
  const authorColors = useColors([...messageAuthors, ...room.members]);
  const barRef = useRef<HTMLTextAreaElement | null>(null);

  return (
    <div className="bg-[#101c2c] text-white font-sans p-6 lg:max-w-5xl mx-auto space-y-12">
      {/* Breadcrumb */}
      {/* <div>
        <BreadcrumbGroup
          items={[{ text: 'Home', href: '/' }]}
          onFollow={(e) => {
            e.preventDefault();
            navigate(e.detail.href);
          }}
        />
      </div> */}

      {/* Hero Section */}
      <div className="bg-[#1e2a38] p-8 rounded-lg shadow-lg flex flex-col lg:flex-row items-center lg:space-x-6">
        <img src={logoUrl} alt="Escape Events Logo" className="max-w-48 h-auto w-64 mb-6 lg:mb-0" />
        <div className="flex flex-col text-center lg:text-left">
          <h1 className="text-4xl font-bold mb-6">Escape Events</h1>
          <p className="text-lg mb-4">
            Welcome to <b>Escape Events</b>, the premier AI-powered escape room platform for
            corporate events. Engage in thrilling experiences designed to foster teamwork and
            problem-solving skills.
          </p>
        </div>
      </div>

      {/* Two Columns: What is Escape Events? | Who is Escape Events? */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="bg-[#1e2a38] p-8 rounded-lg shadow-lg">
          <h2 className="text-3xl font-bold mb-4">What is Escape Events?</h2>
          <p className="text-lg">
            Escape Events is a platform that enables you to host escape room events for your team or
            organization. From small get-togethers to large corporate events, we've got you covered.
            Create a scenario, invite your co-workers, and work together in real-time to solve
            puzzles and escape the room. Perfect for team-building!
          </p>
        </div>
        <div className="bg-[#1e2a38] p-8 rounded-lg shadow-lg">
          <h2 className="text-3xl font-bold mb-4">Who is Escape Events?</h2>
          <p className="text-lg">
            We are the creators of the very first AI-powered escape room platform{' '}
            <Link href="https://rooms.aiescape.io" target="_blank" external>
              <span className="text-base">AI Escape</span>
            </Link>
            , now with over 2,000 players! We also consult with companies through{' '}
            <Link href="https://consulting.aiescape.io" target="_blank" external>
              <span className="text-base">AI Escape Consulting</span>
            </Link>
            , helping them integrate AI into their businesses. With over 10 years of experience in
            AI, we can help you create the perfect AI-powered escape room for your team or
            organization.
          </p>
        </div>
      </div>

      <div>
        <h2 className="text-3xl font-bold mb-4">See for yourself!</h2>
        <p className="text-lg mb-4">
          We've created the following scenario as an example of what you can experience with Escape
          Events. Check it out!
        </p>
        <blockquote className="border-l-4 border-blue-500 pl-4 text-lg italic text-gray-300 bg-blue-950 p-4 rounded-lg shadow-lg mb-4">
          Create an escape room that teaches about our company's stance on cybersecurity
        </blockquote>
        {/* Example Scenario Section */}
        <div className="bg-[#1e2a38] p-8 rounded-lg shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <img
              src={scenario.image}
              alt={`${scenario.name} Initial Scene`}
              className="w-full h-64 object-cover rounded-lg"
            />
            <div className="flex flex-col justify-center space-y-4">
              <h2 className="text-2xl font-bold">{scenario.name}</h2>
              <p className="text-gray-400">{scenario.description}</p>
              {scenario.stages.length > 0 && <StageGraph stages={scenario.stages} />}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <ChatBox messages={room.messages} authorColors={authorColors} stages={room.stages} />
          <div className="mb-8" />
          <div className="px-4 pb-4 bg-transparent">
            <div className="mx-auto lg:max-w-4xl bg-transparent">
              <ChatBar
                addMessage={() => {
                  return Promise.resolve();
                }}
                addAudio={() => {
                  return Promise.resolve();
                }}
                loading={false}
                textareaRef={barRef}
                placeholder="This is where you'd enter your next step and try to escape!"
              />
            </div>
          </div>
        </div>
      </div>

      <footer className="text-center p-6 bg-[#101c2c]">
        <h2 className="text-2xl font-bold mb-4">Want to give it a try?</h2>
        <p className="mb-4">Schedule a demo today:</p>
        {/* Teams meeting is offset to the right whe wrapping */}
        <div className="flex justify-center space-x-6 mb-4">
          <a
            className="flex items-center border-2 border-blue-950 rounded-lg p-2 text-blue-400 hover:text-blue-300 h-16 hover:bg-blue-950"
            href="https://clients.aiescape.io/meetings/max-weinzierl/consultation"
          >
            <svg className="mr-2 flex-shrink-0" height="30" width="30">
              <image
                height="30"
                width="30"
                xlinkHref="https://developers.zoom.us/svgs/favicon.svg"
              />
            </svg>
            <span>Zoom Meeting</span>
          </a>
          <a
            className="flex items-center border-2 border-blue-950 rounded-lg p-2 text-blue-400 hover:text-blue-300 h-16 hover:bg-blue-950"
            href="https://clients.aiescape.io/meetings/max-weinzierl/consultation-teams"
          >
            <svg className="mr-2 flex-shrink-0" height="30" width="30">
              <image
                height="30"
                width="30"
                xlinkHref="https://upload.wikimedia.org/wikipedia/commons/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg"
              />
            </svg>
            <span>Teams Meeting</span>
          </a>
        </div>
        <p className="mb-4">
          Contact us at{' '}
          <a className="text-blue-400 hover:text-blue-300" href="mailto:rooms@aiescape.io">
            rooms@aiescape.io
          </a>{' '}
          for more information!
          <br />
          We can get you a quote for your next event in no time.
        </p>
        <p className="text-sm">© 2024 AI Escape LLC - All Rights Reserved</p>
      </footer>
    </div>
  );
}
